import React, { useEffect, useState } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import styles from './Filters.module.scss';
import Filter from './Filter';
import ClearAll from './Controls/ClearAll';
import { RootState } from 'MyTypes';
import { HydratedCurriculumType } from '../../../Common/types/AppConfigTypes';
import * as appConfigSelectors from '../../../AppConfig/appConfigSelectors';
import * as filterSelectors from '../../filterSelectors';
import * as listSelectors from '../../../List/listSelectors';
import * as listActions from '../../../List/listActions';

interface FiltersProps {
    curriculumType: HydratedCurriculumType;
}

const mapDispatchToProps = (dispatch: Dispatch) =>
    bindActionCreators(
        {
            getSearchList: listActions.getSearchList,
            getAZList: listActions.getAZList,
        },
        dispatch,
    );

const mapStateToProps = (state: RootState) => ({
    appConfig: appConfigSelectors.appConfigSelector(state),
    isSearchResults: listSelectors.isSearchResultListSelector(state),
    dynamicFilterValues: filterSelectors.dynamicFiltersSelector(state),
});

type Props = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps> &
    FiltersProps;

type FilterOpenState = {
    [idx: number]: boolean;
};

const Filters: React.FC<Props> = ({
    appConfig,
    curriculumType,
    dynamicFilterValues,
    isSearchResults,
    getSearchList,
    getAZList,
}) => {
    useEffect(() => {
        if (isSearchResults) {
            getSearchList();
        } else {
            getAZList();
        }
    }, [dynamicFilterValues, isSearchResults, getSearchList, getAZList]);

    const [filtersOpenState, setFiltersOpenState] = useState<FilterOpenState>(
        {},
    );

    const defaultOpenState = () => {
        const state: FilterOpenState = {};

        Object.keys(dynamicFilterValues).forEach(filterIdx => {
            const dynamicFilter = dynamicFilterValues as {
                [key: number]: string;
            };
            if (filterIdx) {
                const idx = parseInt(filterIdx);

                state[idx] = dynamicFilter[idx] !== undefined;
            }
        });

        return state;
    };

    useEffect(() => {
        // Initialize Filter Open State
        if (dynamicFilterValues === undefined) {
            return;
        }
        setFiltersOpenState(defaultOpenState());
    }, [dynamicFilterValues, curriculumType]);

    const setFilterOpenStateAtIndex = (open: boolean, idx: number) => {
        const newFilterOpenState = {
            ...filtersOpenState,
            [idx]: open,
        };
        setFiltersOpenState(newFilterOpenState);
    };

    if (
        !appConfig ||
        !curriculumType.filters ||
        !curriculumType.filters.length ||
        !dynamicFilterValues
    ) {
        return null;
    }

    const closeAllFilters = () => setFiltersOpenState({});

    return (
        <div className={styles.filtersContainer}>
            <div className={styles.filtersHeader}>Filter by</div>
            <div className={styles.filterControlsContainer}>
                {curriculumType.filters.map(filter => {
                    const dynamicFilterValue = dynamicFilterValues as {
                        [key: number]: string;
                    };

                    return (
                        <Filter
                            key={filter.index}
                            filter={filter}
                            currentContentType={curriculumType.contentType}
                            dynamicFilterIndex={filter.index}
                            currentValue={dynamicFilterValue[filter.index]}
                            setFilterOpenState={setFilterOpenStateAtIndex}
                            isOpen={filtersOpenState[filter.index]}
                        />
                    );
                })}
            </div>
            <div className={styles.filterClearContainer}>
                <ClearAll closeAllFilters={closeAllFilters} />
            </div>
        </div>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(Filters);
